import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, select, takeEvery } from 'typed-redux-saga';

import { selectIsMeetingRoomIntegration } from '../../../shared/features/config/selectors';
import { parentFrameCommunicator } from '../../../shared/features/parent-frame-communicator';
import { GRID_VIEW } from '../constants';
import { getIsChatLoading } from '../features/chat/slice';
import {
    connectedToConferenceAction,
    disconnectedFromConferenceDoneAction,
    userJoinAction,
    userLeftAction,
} from '../features/conference/slice';
import {
    getActiveGridView,
    getChatWidgetVisible,
    getConferenceId,
    getSelfMutedState,
    getSidebarContent,
    selectMeetingName,
    selectSelfReactionType,
} from '../selectors';
import { DisconnectPayload, SidebarContent, UserParams } from '../types';
import { OutgoingEvents } from './types';

function* handleSelfConnect() {
    const isMeetingRoomIntegration = yield* select(selectIsMeetingRoomIntegration);

    if (isMeetingRoomIntegration) {
        const meetingName = yield* select(selectMeetingName);
        const meetingId = yield* select(getConferenceId);

        yield* call(parentFrameCommunicator.sendMessage, '*', {
            event: OutgoingEvents.MEETING_INFO,
            payload: { meetingName, meetingId },
        });

        const mutedState = yield* select(getSelfMutedState);
        const gridMode = yield* select(getActiveGridView);
        const chatVisible = yield* select(getChatWidgetVisible);
        const sidebarContent = yield* select(getSidebarContent);
        const selfReactionType = yield* select(selectSelfReactionType);
        const participantsListVisible = sidebarContent === SidebarContent.Participants;
        const isChatLoading = yield* select(getIsChatLoading);

        yield* call(parentFrameCommunicator.sendMessage, '*', {
            event: OutgoingEvents.UI_STATE_CHANGED,
            payload: {
                videoEnabled: !mutedState?.videoMuted,
                audioEnabled: !mutedState?.audioMuted,
                reaction: selfReactionType || null,
                gridView: gridMode || GRID_VIEW.DEFAULT,
                chatVisible: chatVisible,
                participantsListVisible: participantsListVisible,
                isChatLoading,
            },
        });
    }

    yield* call(parentFrameCommunicator.sendMessage, '*', {
        event: OutgoingEvents.CONNECTED,
        payload: { id: 'self' },
    });
}

function* handleSelfDisconnect({ payload: { error, source } }: PayloadAction<DisconnectPayload>) {
    yield* call(parentFrameCommunicator.sendMessage, '*', {
        event: OutgoingEvents.DISCONNECTED,
        payload: { id: 'self', error, source },
    });
}

function* handleUserJoin({ payload: { id } }: PayloadAction<UserParams>) {
    yield* call(parentFrameCommunicator.sendMessage, '*', {
        event: OutgoingEvents.CONNECTED,
        payload: { id },
    });
}

function* handleUserLeft({ payload: { id } }: PayloadAction<UserParams>) {
    yield* call(parentFrameCommunicator.sendMessage, '*', {
        event: OutgoingEvents.DISCONNECTED,
        payload: { id },
    });
}

export function* handleConnection(): Generator {
    yield* all([
        takeEvery(connectedToConferenceAction.type, handleSelfConnect),
        takeEvery(disconnectedFromConferenceDoneAction.type, handleSelfDisconnect),
        takeEvery(userJoinAction.type, handleUserJoin),
        takeEvery(userLeftAction.type, handleUserLeft),
    ]);
}
